import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchVideoDetail } from '@/features/content/services/videoService';
import { describeJobError, type JobErrorInfo } from '@/shared/api/jobErrors';
import { safeErrorMessage } from '@/shared/utils/errors';
import type { JobError, ProcessingStatus, VideoDetail } from '@/shared/types';
import { fetchVideoStatus } from '@/features/content/services/videoApi';
import { openVideoSocket } from '@/features/content/services/videoSocket';
import { ApiError, getJson } from '@/shared/api/apiClient';
import { updateLibraryItemStatus } from '@/features/library/services/libraryService';
import { isTerminalStatus } from '@/shared/api/backendContract';
import { logger } from '@/shared/utils/logger';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { ErrorPage } from '@/shared/ui/ErrorPage';
import { useCurrentUser } from '@/app/providers/user';
import { useProcessing } from '@/app/providers/processing';
import { ReferenceReviewPanel } from '@/features/references/components/ReferenceReviewPanel';
import { submitFeedback } from '@/features/content/services/feedbackApi';
import { ConfirmGenerationModal } from '@/features/generation/components/ConfirmGenerationModal';
import { describeGeneration } from '@/features/generation/utils/generationMode';
import { InteractiveText } from '@/features/content/components/InteractiveText';
import { ReadingPreferences } from '@/features/content/components/ReadingPreferences';
import { Workpad } from '@/features/content/components/Workpad';
import { WidgetShell } from '@/features/content/components/WidgetShell';
import { ContentAlert } from '@/features/content/components/ContentAlert';
import { ContentHead } from '@/features/content/components/ContentHead';
import { ContentStage } from '@/features/content/components/ContentStage';
import { FeedbackForm } from '@/features/content/components/FeedbackForm';
import { ShareVideoModal } from '@/features/sharing/components/ShareVideoModal';
import { ExportDialog, useExportPresets } from '@/features/exports';
import {
  DefinitionPopover,
  type DefinitionLookup,
} from '@/features/content/components/DefinitionPopover';
import { activeCueAt, parseVtt, type VttCue } from '@/features/content/utils/vttCues';
import { lookUp, normalizeWord } from '@/features/content/services/dictionaryService';
import {
  addNote,
  deleteNote,
  getWorkpadPrefs,
  listNotes,
  setWorkpadPrefs,
  updateNote,
  DEFAULT_WORKPAD_PREFS,
  type Note,
  type WorkpadPrefs,
} from '@/features/content/services/notesService';
import {
  DEFAULT_READING_PREFS,
  getReadingPrefs,
  resetReadingPrefs,
  setReadingPrefs,
  type ReadingPrefs,
} from '@/features/content/services/readingPrefsService';
import { getWatchProgress, setWatchProgress } from '@/features/library/services/watchProgressService';
import { ArrowLeftIcon, Button, ConfirmDialog, Spinner, cx } from '@/shared/ui';
import { formatLongDateTime } from '@/shared/utils/format';

/* Watch position is written at most this often. `timeupdate` fires ~4×/s, and a
   localStorage write per tick is both wasteful and, once this is an endpoint,
   a request per tick. */
const PROGRESS_SAVE_INTERVAL_MS = 5000;

export default function ContentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { jobs, resumeJob, confirmJob, cancelJob, approveReferences, deleteJob, forgetJob } = useProcessing();
  const userId = user?.userId;
  const [video, setVideo] = useState<VideoDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /* Announced politely to assistive tech. Lives here, not in the review panel: approving
     unmounts the panel, and a live region has to exist before its text changes. */
  const [approvalNotice, setApprovalNotice] = useState('');
  const [pollError, setPollError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  // Structured, not a boolean: the failed-state banner renders the billing
  // detail (e.g. insufficient credits) that comes back with a failed resume.
  const [resumeError, setResumeError] = useState<JobErrorInfo | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [exporting, setExporting] = useState(false);
  /* Null unless the engine offers social exports for this finished video; a 404 (the
     feature is off) or any failure leaves it null and the page exactly as before. */
  const exportPresets = useExportPresets(id, video?.status === 'completed' && Boolean(video.videoUrl));
  // Captions: a same-origin Blob URL wrapping the backend-generated WebVTT track,
  // and whether loading it failed. Captions default to on (see the track-mode
  // effect); the user turns them off via the player's native controls menu.
  const [captionsUrl, setCaptionsUrl] = useState<string | null>(null);
  const [captionError, setCaptionError] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  // Set by the library card's `Report an issue`, which navigates here asking for
  // the feedback form rather than dropping the user at the top of the page.
  const feedbackRef = useRef<HTMLFormElement>(null);
  const reportFocus = (useLocation().state as { focus?: string } | null)?.focus;
  const [feedbackSent, setFeedbackSent] = useState(false);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    },
    [],
  );
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pollerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // The id whose captions we've already resolved from a fresh status fetch (the
  // initial load when already completed, or the reconcile effect below), so a
  // cinematic job (legitimately no vttUrl) doesn't refetch on every render.
  const vttReconciledRef = useRef<string | null>(null);
  // The id we've already defaulted captions ON for, so a rotating captionsUrl
  // (fetchVideoDetail returns a freshly-presigned vttUrl each mount) doesn't
  // re-force captions on after the user turned them off. See the effect below.
  const captionDefaultIdRef = useRef<string | null>(null);

  /* ---- Iteration 2.0: transcript, workpad, reading preferences ---- */
  const [cues, setCues] = useState<VttCue[]>([]);
  const [activeCueId, setActiveCueId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [lookup, setLookup] = useState<DefinitionLookup | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [workpadPrefs, setWorkpadPrefsState] = useState<WorkpadPrefs>(DEFAULT_WORKPAD_PREFS);
  const [readingPrefs, setReadingPrefsState] = useState<ReadingPrefs>(DEFAULT_READING_PREFS);
  const [textCollapsed, setTextCollapsed] = useState(false);
  const [notesCollapsed, setNotesCollapsed] = useState(false);
  /* Theater lives here rather than in the player because it is a page-level
     layout change: the side column steps aside so the video can take the width. */
  const [theater, setTheater] = useState(false);
  const lastProgressSaveRef = useRef(0);

  /* Resuming where the viewer left off, which is the whole point of recording a
     position and had never once worked.

     Nothing read the saved position back, so playback always began at 0 — and
     then destroyed the record on the way past: `timeupdate` fires within a
     second of opening, `lastProgressSaveRef` starts at 0 so the interval test
     passes immediately, and a save at ~0 seconds is below watchProgressService's
     MIN_FRACTION, which is the branch that *deletes* the entry. Half-watched
     video, opened and closed again without touching anything: the bar on its
     library card is gone.

     Two things have to line up before the seek can happen — the stored position
     has to arrive, and the element has to know its own duration — and either can
     be second. Both call `applyResume`, and the one that finds the other side
     ready does the work. Until it has run, saving is refused outright: that is
     the guard the first tick used to walk straight through. */
  const resumeAtRef = useRef<number | null>(null);
  const resumeReadyRef = useRef(false);

  const applyResume = useCallback(() => {
    const element = videoRef.current;
    const position = resumeAtRef.current;
    if (!element || position === null) return;
    // No duration yet means no metadata yet, and seeking would be discarded.
    if (!Number.isFinite(element.duration) || element.duration <= 0) return;

    resumeAtRef.current = null;
    element.currentTime = position;
    setCurrentTime(position);
  }, []);

  useEffect(() => {
    resumeAtRef.current = null;
    resumeReadyRef.current = false;
    if (!userId || !id) return;

    let active = true;
    void getWatchProgress(userId)
      .then((progress) => {
        if (!active) return;
        resumeAtRef.current = progress[id]?.position ?? null;
        resumeReadyRef.current = true;
        // The video may already be loaded — a cached entry renders the element
        // before this read lands more often than not.
        applyResume();
      })
      .catch((error: unknown) => {
        /* This read could not fail when it came from localStorage. It is
           `/api/v1/watch-progress` now, so a blip, a 500 or an expired session
           rejects it — and `resumeReadyRef` is what gates *saving* (see the
           timeupdate handler). Left false, the position is never written again
           for the whole visit, which is the write-only bug this branch just
           finished fixing, reintroduced by a dropped request.

           So: no stored position to restore, but recording resumes. Losing
           where they were is a worse outcome than starting them at 0. */
        if (!active) return;
        logger.warn('Could not read watch progress; starting from the beginning', error);
        resumeAtRef.current = null;
        resumeReadyRef.current = true;
      });
    return () => {
      active = false;
    };
  }, [userId, id, applyResume]);
  /* After a successful delete there is nothing at this route any more, so the
     page leaves rather than rendering a 404 for a video the user just removed.
     Stopping the poller first matters: it would otherwise tick once more
     against a job that no longer exists and paint an error on the way out. */
  const redirectAfterDelete = useCallback(() => {
    if (pollerRef.current) {
      clearInterval(pollerRef.current);
      pollerRef.current = null;
    }
    if (id) forgetJob(id);
    navigate('/library', { replace: true });
  }, [forgetJob, id, navigate]);

  useEffect(() => {
    if (!id) {
      setLoadError('Missing video identifier.');
      return;
    }
    if (!userId) {
      return;
    }

    let mounted = true;
    fetchVideoDetail(userId, id)
      .then((result) => {
        if (!mounted) return;
        if (!result) {
          redirectAfterDelete();
          return;
        }
        const { detail, statusFresh } = result;
        setVideo(detail);
        // Spare the reconcile effect below a duplicate fetch only when this
        // 'completed' status came from a *fresh* backend read — that read checks
        // vttUrl too (videoService retries a missing one), so its answer (a URL or
        // a confirmed absence) is authoritative. A stale-cache fallback (the
        // refresh threw) leaves statusFresh false, so we don't mark reconciled and
        // the effect below re-fetches with retries; likewise a still-processing
        // video stays unmarked so the effect can backfill vttUrl once it completes
        // over the socket (whose events omit vtt_url).
        if (detail.status === 'completed' && statusFresh) {
          vttReconciledRef.current = id;
        }
      })
      .catch((err) => {
        if (!mounted) return;
        if (err instanceof ApiError && err.status === 404) {
          redirectAfterDelete();
          return;
        }
        setLoadError('Unable to load this video. Please try again.');
      });

    return () => {
      mounted = false;
    };
  }, [id, redirectAfterDelete, userId]);

  const videoStatus = video?.status;
  /* The effect below keys on this boolean, not on `videoStatus` itself. The
     socket it opens is what changes the status, so with the status in the deps
     every transition (queued → awaiting_confirmation → processing → …) tore the
     socket down and re-handshook it, resetting the retry count and dropping any
     frame pushed in the gap — and if the socket had already fallen back to REST,
     the cleanup killed the 15s poller only for the re-run to start a brand-new
     socket, so the fallback never stuck. Active → terminal is the only edge the
     effect actually needs to see. */
  const videoActive = Boolean(videoStatus) && !isTerminalStatus(videoStatus!);

  /* ProcessingProvider already holds exactly one socket per non-terminal job,
     for every tab. Opening a second one here for the job the user happens to be
     looking at doubled that: N active jobs with a detail page open was N+1
     sockets per tab, and each socket costs a Postgres backend on the engine
     side. The provider's copy is also the better-informed one — it merges the
     quote fields this page used to drop, which is why the sidebar's confirmation
     dialog advanced when this one sat on its spinner.

     So the socket here is now the fallback rather than the default: it only
     opens for a job the provider is not tracking, which is the case for a video
     opened directly by URL before hydration has caught up. `providerTracksJob`
     keys the effect, so gaining provider coverage tears this socket down. */
  const providerTracksJob = Boolean(
    id && jobs.some((job) => job.id === id && !isTerminalStatus(job.status)),
  );
  const needsOwnSocket = videoActive && !providerTracksJob;

  /* Merge one status update into the page. Shared by the two things that can
     produce one: the provider's job (the usual case) and this page's own
     fallback socket/poller (only when the provider is not tracking the job). */
  const applyResult = useCallback(
    (result: {
      status: ProcessingStatus;
      videoUrl?: string;
      thumbnailUrl?: string;
      title?: string;
      error?: JobError | null;
      estimatedCost?: number;
      estimatedDurationSeconds?: number;
    }) => {
      setPollError(null);
      setVideo((prev) => {
        if (!prev) return prev;
        if (prev.status === 'cancelled') return prev;
        const updated: VideoDetail = {
          ...prev,
          status: result.status,
          title: result.title ?? prev.title,
          videoUrl: result.videoUrl ?? prev.videoUrl,
          thumbnailUrl: result.thumbnailUrl ?? prev.thumbnailUrl,
          error: result.error ?? prev.error,
          /* The quote, which this used to drop on the floor.
             A job can reach `awaiting_confirmation` before the engine has priced
             it, and `generationPhase` reads an absent cost as "still quoting" —
             so the dialog sits on its spinner. The REST tick *does* carry
             `estimated_cost`; not merging it here meant the quote could arrive
             and never be shown, leaving cancel as the only way out of a job the
             user meant to confirm. ProcessingProvider has always merged both
             fields, which is why the sidebar's copy of this dialog advanced and
             this one did not. */
          estimatedCost: result.estimatedCost ?? prev.estimatedCost,
          estimatedDurationSeconds:
            result.estimatedDurationSeconds ?? prev.estimatedDurationSeconds,
        };
        return updated;
      });
    },
    [],
  );

  /* The normal path: follow the provider's copy of this job. It is fed by the
     one socket the provider already holds, so this page costs no connection at
     all for a job that is in the queue. */
  const trackedJob = id ? jobs.find((job) => job.id === id) : undefined;
  useEffect(() => {
    if (!trackedJob) return;
    applyResult({
      status: trackedJob.status,
      videoUrl: trackedJob.videoUrl,
      thumbnailUrl: trackedJob.thumbnailUrl,
      title: trackedJob.title,
      error: trackedJob.error,
      estimatedCost: trackedJob.estimatedCost,
      estimatedDurationSeconds: trackedJob.estimatedDurationSeconds,
    });
  }, [trackedJob, applyResult]);

  useEffect(() => {
    if (!id || !needsOwnSocket) {
      if (pollerRef.current) {
        clearInterval(pollerRef.current);
        pollerRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const closeSocket = openVideoSocket(
      id,
      applyResult,
      () => {
        if (cancelled) return;
        setPollError('Live connection unavailable — checking for updates.');
        const pollOnce = async () => {
          try {
            const status = await fetchVideoStatus(id);
            if (cancelled) return;
            applyResult(status);
            if (isTerminalStatus(status.status)) {
              if (pollerRef.current) {
                clearInterval(pollerRef.current);
                pollerRef.current = null;
              }
              setPollError(null);
            }
          } catch (err) {
            if (cancelled) return;
            if (err instanceof ApiError && err.status === 404) {
              redirectAfterDelete();
              return;
            }
            setPollError('Status check failed — retrying automatically.');
          }
        };
        // Poll immediately so there's no ~15s frozen gap after the WS gives up
        // (on top of the ~15s of WS backoff already elapsed).
        void pollOnce();
        // Guard against a second onFallback overwriting (and orphaning) the interval.
        if (pollerRef.current === null) {
          pollerRef.current = setInterval(pollOnce, 15_000);
        }
      },
    );

    return () => {
      cancelled = true;
      closeSocket();
      if (pollerRef.current) {
        clearInterval(pollerRef.current);
        pollerRef.current = null;
      }
    };
  }, [id, needsOwnSocket, userId, applyResult, redirectAfterDelete]);

  /* Mirror a terminal status to the library cache from *committed* state. This
     write lived inside the setVideo updater above, which must stay pure: React
     StrictMode double-invokes updaters, and under concurrent rendering an
     updater can run for a render React then throws away — persisting to
     localStorage a status the UI never showed. An effect only ever sees state
     that actually committed. */
  const videoUrlCommitted = video?.videoUrl;
  const thumbnailUrlCommitted = video?.thumbnailUrl;
  useEffect(() => {
    if (!userId || !id || !videoStatus || !isTerminalStatus(videoStatus)) return;
    // Keys only when present: updateLibraryItemStatus spreads these over the
    // cached entry, and an explicit `videoUrl: undefined` would blank a URL the
    // cache already holds.
    updateLibraryItemStatus(userId, id, videoStatus, {
      ...(videoUrlCommitted ? { videoUrl: videoUrlCommitted } : {}),
      ...(thumbnailUrlCommitted ? { thumbnailUrl: thumbnailUrlCommitted } : {}),
    });
  }, [userId, id, videoStatus, videoUrlCommitted, thumbnailUrlCommitted]);

  // Reconcile the caption track once a video is completed but has no vttUrl. The
  // realtime WsJobStatusEvent carries no vtt_url, so a job that completes live over
  // the socket lands here without a caption URL — a REST status fetch backfills it.
  // The per-id ref is set only after a *successful* fetch (whether or not it had a
  // vttUrl), so a transient error retries rather than permanently suppressing
  // captions for the session; the initial-load handler above sets it when the
  // video was already completed on a *fresh* load, so this doesn't duplicate that
  // fetch (but a stale-cache load leaves it unset so this effect still runs).
  useEffect(() => {
    if (!id || !userId) return;
    if (videoStatus !== 'completed' || video?.vttUrl) return;
    if (vttReconciledRef.current === id) return;

    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const attempt = async (retriesLeft: number) => {
      try {
        const latest = await fetchVideoStatus(id);
        if (!active) return;
        // Definitive answer — don't reconcile this id again.
        vttReconciledRef.current = id;
        if (latest.vttUrl) {
          setVideo((prev) => (prev ? { ...prev, vttUrl: latest.vttUrl } : prev));
          updateLibraryItemStatus(userId, id, latest.status, { vttUrl: latest.vttUrl });
        }
      } catch (err) {
        if (!active) return;
        if (err instanceof ApiError && err.status === 404) {
          redirectAfterDelete();
          return;
        }
        // Transient failure: retry a few times before giving up. The ref stays
        // unset so a later reload/navigation can still reconcile.
        if (!active || retriesLeft <= 0) return;
        retryTimer = setTimeout(() => void attempt(retriesLeft - 1), 2000);
      }
    };
    void attempt(2);

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [id, redirectAfterDelete, userId, videoStatus, video?.vttUrl]);






  const handleRetry = async () => {
    if (!id || resuming) return;
    setResuming(true);
    setResumeError(null);
    try {
      await resumeJob(id);
      // Flip to a polled state so the status effect resumes polling for completion.
      // Clear the stale failure detail so the "processing" state renders cleanly.
      setVideo((prev) => (prev ? { ...prev, status: 'processing', error: null } : prev));
    } catch (err) {
      // Inline error only — don't call setLoadError, which would replace the
      // whole page with the error view and lose the loaded video. Billing-aware:
      // insufficient balance / no card get a wallet link (see describeJobError).
      setResumeError(describeJobError(err));
    } finally {
      setResuming(false);
    }
  };

  const handleCancel = async () => {
    if (!id || cancelling) return;
    setConfirmingCancel(false);
    setCancelling(true);
    setCancelError(false);
    try {
      await cancelJob(id);
      setVideo((prev) => (prev ? { ...prev, status: 'cancelled' } : prev));
    } catch {
      setCancelError(true);
    } finally {
      setCancelling(false);
    }
  };

  const handleDelete = async () => {
    if (!id || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteJob(id);
      /* No `setConfirmingDelete(false)` — the redirect below unmounts this page,
         and closing the dialog first paints one frame of the deleted video
         still sitting there. On failure the dialog deliberately stays open. */
      redirectAfterDelete();
    } catch (err) {
      setDeleteError(
        `${safeErrorMessage(err, 'Could not delete this video.')} It is still here — please try again.`,
      );
    } finally {
      setDeleting(false);
    }
  };

  // Download is a two-step, ownership-checked flow: an authenticated GET (carries
  // the JWT) mints a fresh, attachment-dispositioned presigned S3 URL, then we
  // navigate to it. S3's Content-Disposition forces a save without unloading the
  // page. Minting per click avoids ever following a stale (>1h) presigned link.
  const handleDownload = async () => {
    if (!id || downloading) return;
    setDownloading(true);
    setDownloadError(false);
    try {
      const { url } = await getJson<{ url: string }>(`/api/v1/videos/${id}/download`);
      if (!url) throw new Error('No download URL returned');
      window.location.href = url;
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  };

  const handleFeedbackSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!id || !feedbackText.trim() || feedbackSubmitting) return;
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      await submitFeedback(feedbackText.trim(), id);
      setFeedbackText('');
      setFeedbackSent(true);
      // Auto-clear the confirmation after a few seconds so the widget is ready
      // for another note without the user having to dismiss anything. Held in a
      // ref and cleared on unmount: a fire-and-forget timer here outlives the
      // page, and a second submission inside the window would race the first.
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = setTimeout(() => setFeedbackSent(false), 4000);
    } catch (submissionError) {
      setFeedbackError(safeErrorMessage(submissionError, 'Could not send feedback'));
    } finally {
      setFeedbackSubmitting(false);
    }
  };


  // Captions come from the backend as a presigned WebVTT URL (storytelling, completed
  // jobs only). We fetch the .vtt and wrap it in a same-origin Blob URL for the
  // <track>, rather than pointing <track src> at the cross-origin S3 URL directly:
  // a cross-origin track would force crossOrigin="anonymous" onto <video>, which in
  // turn would require CORS headers on the mp4 itself and could break playback.
  // Revoke the previous Blob URL on change/unmount so we don't leak blobs.
  const vttUrl = video?.vttUrl;
  useEffect(() => {
    if (!vttUrl) {
      setCaptionsUrl(null);
      setCaptionError(false);
      setCues([]);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    // Clear any previous track immediately so navigating between videos doesn't
    // leave <track> pointing at a Blob URL we're about to revoke.
    setCaptionsUrl(null);
    setCaptionError(false);
    setCues([]);
    /* A bare fetch, deliberately, and the only one left in the app. `vttUrl` is
       a presigned S3 URL (docs/captions-contract.md), not an engine endpoint —
       routing it through apiClient would attach our Authorization header, which
       sends the user's JWT to a third-party host and can invalidate the
       signature. The presigned URL carries its own auth in the query string. */
    fetch(vttUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`VTT fetch failed: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        // Parsed once and asked twice. A header-only or blank-payload track
        // (e.g. a storytelling job with no narration) yields no cues, and a
        // track with no cues has nothing to show — so leave captionsUrl null
        // and the <track>, with the native captions control, stays hidden.
        const parsed = parseVtt(text);
        if (parsed.length === 0) {
          setCaptionsUrl(null);
          return;
        }
        objectUrl = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
        setCaptionsUrl(objectUrl);
        setCues(parsed);
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error('Caption track failed to load:', err);
        setCaptionsUrl(null);
        setCaptionError(true);
        setCues([]);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [vttUrl]);

  // Default captions ON the first time a track is available for a given video,
  // then never again for that video. Gated by id (not captionsUrl): fetchVideoDetail
  // returns a freshly-presigned vttUrl on every mount, so the blob URL rotates —
  // keying on captionsUrl would re-force captions on and clobber the user's choice
  // from the player's native controls menu. If the TextTrack isn't attached yet
  // (Chromium attaches it synchronously, but be defensive), wait for `addtrack`
  // rather than silently leaving captions off.
  useEffect(() => {
    if (!captionsUrl || !id) return;
    if (captionDefaultIdRef.current === id) return;
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const showDefault = () => {
      const track = videoEl.textTracks?.[0];
      if (!track) return false;
      captionDefaultIdRef.current = id;
      track.mode = 'showing';
      return true;
    };

    if (showDefault()) return;

    const tracks = videoEl.textTracks;
    const onAddTrack = () => {
      if (showDefault()) tracks.removeEventListener('addtrack', onAddTrack);
    };
    tracks.addEventListener('addtrack', onAddTrack);
    return () => tracks.removeEventListener('addtrack', onAddTrack);
  }, [captionsUrl, id]);

  /* Both routes to the feedback form run this: the library card's `Report an
     issue`, which navigates here asking for it, and the same item in this page's
     own `···` menu. That second one used to expand the Notes workpad instead —
     a different panel, on the other side of the page, that has nothing to do
     with reporting anything. */
  const focusFeedback = useCallback(() => {
    const form = feedbackRef.current;
    if (!form) return;
    form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    form.querySelector<HTMLTextAreaElement>('textarea')?.focus();
  }, []);

  /* Depends on `video` because the form is behind the loading early-return, so
     the ref is null until the detail arrives. */
  useEffect(() => {
    if (reportFocus !== 'feedback' || !video) return;
    focusFeedback();
  }, [reportFocus, video, focusFeedback]);

  /* The transcript used to live in its own effect that re-fetched `captionsUrl`
     — the Blob the effect above had just built — only to parse text that effect
     already held. It read as a race (a fetch against a URL another cleanup
     revokes) but it never was one: reading a Blob URL is local, and the `active`
     flag returned before logging. It was simply the same work twice, so the
     parse now happens where the text already is. The transcript still comes from
     exactly the bytes the <track> renders, which is what kept the two in
     agreement in the first place. */

  useEffect(() => {
    if (!userId || !id) return;
    let active = true;
    /* Three reads that could not reject when they were localStorage. As HTTP
       they can, independently — and they feed three unrelated pieces of state,
       so `allSettled` rather than `all`: under `all` a flaky preferences
       endpoint threw away the notes that had just arrived successfully, which
       is the worst of the three to lose and the one the user would notice.

       Each result lands on its own. Whatever fails keeps the initial state the
       component already renders with, so a partial failure is a screen missing
       one thing rather than a workpad indistinguishable from a user who has
       never taken a note. */
    void Promise.allSettled([
      listNotes(userId, id),
      getWorkpadPrefs(userId),
      getReadingPrefs(userId),
    ]).then(([notesRead, padRead, readingRead]) => {
      if (!active) return;
      if (notesRead.status === 'fulfilled') setNotes(notesRead.value);
      if (padRead.status === 'fulfilled') setWorkpadPrefsState(padRead.value);
      if (readingRead.status === 'fulfilled') setReadingPrefsState(readingRead.value);

      const failures = [notesRead, padRead, readingRead].filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (failures.length > 0) {
        logger.error(
          'Could not load notes or workpad preferences',
          failures.map((failure) => failure.reason),
        );
      }
    });
    return () => {
      active = false;
    };
  }, [userId, id]);

  const handleTimeUpdate = () => {
    const element = videoRef.current;
    if (!element) return;

    const time = element.currentTime;
    /* Whole-second granularity. `timeupdate` fires ~4×/s, and a state set per
       tick re-rendered the entire page — the per-word transcript, the workpad,
       the stage — for the length of every video. Everything this state feeds
       (the workpad's note timestamps and its m:ss button label) reads whole
       seconds anyway; returning `prev` bails React out of the render. The
       active-cue set below runs every tick but only changes on a cue boundary,
       which is the same bail-out. */
    setCurrentTime((prev) => (Math.floor(time) === Math.floor(prev) ? prev : time));
    setActiveCueId(activeCueAt(cues, time)?.id ?? null);

    /* Not until the stored position has been read *and* either restored or
       found absent. Saving before that overwrites the very thing we are about
       to seek to — see the resume block above. */
    if (!resumeReadyRef.current || resumeAtRef.current !== null) return;

    const now = Date.now();
    if (userId && id && now - lastProgressSaveRef.current > PROGRESS_SAVE_INTERVAL_MS) {
      lastProgressSaveRef.current = now;
      void setWatchProgress(userId, id, time, element.duration);
    }
  };

  /* Stable identities, so the memoised transcript sees the same props between
     cue boundaries and skips its 300-line render. */
  const seekTo = useCallback((seconds: number) => {
    const element = videoRef.current;
    if (!element) return;
    element.currentTime = seconds;
    setCurrentTime(seconds);
  }, []);

  /* Lookups are network calls now, so they can land out of order and they can
     land after the viewer has moved on. Each tap takes a ticket; a reply that is
     not holding the current one is dropped. Closing the card burns the ticket
     too, otherwise an in-flight lookup reopens a card the viewer just dismissed. */
  const lookupTicket = useRef(0);

  const closeLookup = () => {
    lookupTicket.current += 1;
    setLookup(null);
  };

  const handleWordSelect = useCallback(async (raw: string) => {
    const ticket = (lookupTicket.current += 1);
    const word = normalizeWord(raw);
    setLookup({ word, status: 'loading' });

    try {
      const entry = await lookUp(raw);
      if (ticket !== lookupTicket.current) return;
      setLookup(entry ? { word, status: 'found', entry } : { word, status: 'missing' });
    } catch {
      if (ticket !== lookupTicket.current) return;
      setLookup({ word, status: 'error' });
    }
  }, []);

  const selectWord = useCallback(
    (word: string) => void handleWordSelect(word),
    [handleWordSelect],
  );

  /* Note mutations run through one queue. Each of the service calls below is a
     read-modify-write of the same stored array, and two in flight at once drop
     one of the writes: a note's blur-commit races the delete click that caused
     the blur, and the delete — reading the pre-commit array — persists the edit
     out of existence. Today the service is synchronous localStorage and the
     microtask ordering happens to save us; the moment notesService becomes the
     endpoint the integration doc describes, it stops doing so. The queue makes
     the ordering a property of the code rather than of the store's latency. */
  const noteOpsRef = useRef(Promise.resolve());
  const runNoteOp = useCallback((op: () => Promise<Note[]>) => {
    const run = noteOpsRef.current.then(op).then(setNotes);
    // The queue must survive a failed op; the failure still reaches nobody
    // today (matching the previous fire-and-forget), just without wedging it.
    noteOpsRef.current = run.catch(() => undefined);
    return run;
  }, []);

  const updatePad = async (next: Partial<WorkpadPrefs>) => {
    if (!userId) return;
    setWorkpadPrefsState(await setWorkpadPrefs(userId, next));
  };

  const updateReading = async (next: Partial<ReadingPrefs>) => {
    if (!userId) return;
    setReadingPrefsState(await setReadingPrefs(userId, next));
  };

  if (loadError) {
    return (
      <ErrorPage
        title="Video unavailable"
        message={loadError}
        backLabel="Back to Library"
        backTo="/library"
      />
    );
  }

  if (!video) {
    return (
      <div className="content-page__loading">
        <Spinner size={26} label="Loading video details" />
      </div>
    );
  }

  /* A job parked for character review is still a live generation holding credits,
     so it is cancellable like a rendering one and not deletable. */
  const isRendering =
    video.status === 'processing' ||
    video.status === 'queued' ||
    video.status === 'awaiting_reference_review';
  const canShare = video.status === 'completed' && Boolean(video.videoUrl);

  /* The live stage counts, straight from ProcessingProvider — which already owns
     the per-job WebSocket and the REST fallback. This page polls `/videos/:id`
     for status, but that response carries no progress breakdown, so the detail
     has to come from the job the provider is already tracking. Absent for a
     video opened from the library in a later session, which is why the stage
     falls back to the plain spinner rather than an empty checklist. */
  const jobProgress = jobs.find((job) => job.id === id)?.progress;
  // Routed through the shared formatter, which guards an unparseable value —
  // this call site had none, so a bad timestamp rendered "Invalid Date".
  const createdLabel = formatLongDateTime(video.createdAt) || null;

  return (
    <section className="content-page">
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {approvalNotice}
      </p>
      {pollError ? <ErrorBanner message={pollError} onDismiss={() => setPollError(null)} /> : null}

      <div className="content-page__top">
        <Link to="/library" className="content-page__back" aria-label="Back to library">
          <ArrowLeftIcon size={18} />
        </Link>
      </div>

      <ContentHead
        title={video.title}
        createdLabel={createdLabel}
        details={describeGeneration(video.metadata)}
        canDownload={Boolean(video.videoUrl)}
        downloading={downloading}
        isRendering={isRendering}
        cancelling={cancelling}
        deleting={deleting}
        onDownload={() => void handleDownload()}
        onShare={canShare ? () => setSharing(true) : undefined}
        onExport={exportPresets ? () => setExporting(true) : undefined}
        onReportIssue={focusFeedback}
        onCancel={() => setConfirmingCancel(true)}
        onDelete={() => {
          setDeleteError(null);
          setConfirmingDelete(true);
        }}
      />
      <ShareVideoModal open={sharing} videoId={video.id} title={video.title} onClose={() => setSharing(false)} />
      {exportPresets ? (
        <ExportDialog
          open={exporting}
          videoId={video.id}
          presets={exportPresets}
          onClose={() => setExporting(false)}
        />
      ) : null}

      {video.description ? <p className="content-page__desc">{video.description}</p> : null}

      {video.status === 'awaiting_confirmation' ? (
        <ConfirmGenerationModal
          open
          status={video.status}
          estimatedCost={video.estimatedCost}
          estimatedDurationSeconds={video.estimatedDurationSeconds}
          onConfirm={async () => {
            await confirmJob(video.id);
            /* Flip locally, as the retry path does. `confirmJob` resolves without
               handing back the status, and this dialog is rendered off
               `video.status` — so without this the modal stayed open on the
               confirm step with its button live again, and a second press sent a
               second confirm for a job already accepted. The socket corrects the
               exact status a tick later. */
            setVideo((prev) => (prev ? { ...prev, status: 'processing' } : prev));
          }}
          onCancel={async () => {
            await cancelJob(video.id);
            setVideo((prev) => (prev ? { ...prev, status: 'cancelled' } : prev));
          }}
        />
      ) : null}

      {video.status === 'awaiting_reference_review' ? (
        <ReferenceReviewPanel
          jobId={video.id}
          approve={async (jobId, reviewedVersions) => {
            await approveReferences(jobId, reviewedVersions);
            /* Flip locally, exactly as confirm does: the panel is rendered off
               `video.status`, so without this it would stay up with its button live
               again until the socket caught up. The worker's own status event
               corrects the exact value a moment later. */
            setVideo((prev) => (prev ? { ...prev, status: 'queued' } : prev));
            setApprovalNotice('Characters approved. Your video is now rendering.');
          }}
        />
      ) : null}

      {video.status === 'failed' ? (
        <div className="content-page__alert" role="alert">
          <span>
            {video.error?.message ?? 'Generation failed before this video finished rendering.'}
          </span>
          <div className="content-page__alert-actions">
            <Button size="sm" onClick={handleRetry} disabled={resuming}>
              {resuming ? 'Resuming…' : 'Retry generation'}
            </Button>
            {resumeError ? (
              <span className="content-page__alert-detail">
                {resumeError.message}
                {resumeError.walletCta ? (
                  <>
                    {' '}
                    <Link to="/wallet">Go to Wallet</Link>
                  </>
                ) : null}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {cancelError ? <ContentAlert>Could not cancel generation. Please try again.</ContentAlert> : null}
      {downloadError ? (
        <ContentAlert>Couldn’t prepare the download. Please try again.</ContentAlert>
      ) : null}
      {captionError ? <ContentAlert>Captions unavailable.</ContentAlert> : null}

      {/* Two columns, as the Content Page frames draw them: the video and the
          transcript stacked in the left column, the workpad — and the definition
          under it — in a fixed 292px column beside them. The video used to sit
          above this row at full width, which pushed the notes below the fold on
          the one screen where you want them next to what you are watching. */}
      <div className={cx('content-page__body', theater && 'is-theater')}>
        <div className="content-page__main">
          <ContentStage
            videoRef={videoRef}
            videoUrl={video.videoUrl}
            thumbnailUrl={video.thumbnailUrl}
            status={video.status}
            progress={jobProgress}
            isDocumentation={video.metadata?.style === 'documentation'}
            captionsUrl={captionsUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={applyResume}
            theater={theater}
            onToggleTheater={() => setTheater((value) => !value)}
          />

          <WidgetShell
            title="Interactive Text"
            collapsed={textCollapsed}
            onToggle={() => setTextCollapsed((value) => !value)}
            className="content-page__transcript"
            actions={
              <ReadingPreferences
                prefs={readingPrefs}
                onChange={(next) => void updateReading(next)}
                onReset={async () => {
                  if (userId) setReadingPrefsState(await resetReadingPrefs(userId));
                }}
              />
            }
          >
            <InteractiveText
              cues={cues}
              activeCueId={activeCueId}
              prefs={readingPrefs}
              onSeek={seekTo}
              onWordSelect={selectWord}
            />
          </WidgetShell>
        </div>

        <div className="content-page__side">
          <WidgetShell
            title="Notes"
            collapsed={notesCollapsed}
            onToggle={() => setNotesCollapsed((value) => !value)}
          >
            <Workpad
              notes={notes}
              prefs={workpadPrefs}
              currentTime={currentTime}
              onSeek={seekTo}
              onPrefsChange={(next) => void updatePad(next)}
              onAddNote={(timestamp, html) => {
                if (!userId || !id) return;
                void runNoteOp(() => addNote(userId, id, timestamp, html));
              }}
              onUpdateNote={(noteId, html) => {
                if (!userId || !id) return;
                void runNoteOp(() => updateNote(userId, id, noteId, html));
              }}
              onDeleteNote={(noteId) => {
                if (!userId || !id) return;
                void runNoteOp(() => deleteNote(userId, id, noteId));
              }}
            />
          </WidgetShell>

          {lookup ? <DefinitionPopover lookup={lookup} onClose={closeLookup} /> : null}
        </div>
      </div>

      {video.sourceText ? (
        <details className="content-page__source">
          <summary>Original text</summary>
          <div className="content-page__source-body">{video.sourceText}</div>
        </details>
      ) : null}

      {id ? (
        <FeedbackForm
          formRef={feedbackRef}
          value={feedbackText}
          onChange={setFeedbackText}
          submitting={feedbackSubmitting}
          sent={feedbackSent}
          error={feedbackError}
          onDismissError={() => setFeedbackError(null)}
          onSubmit={handleFeedbackSubmit}
        />
      ) : null}

      {/* Same wording as the library card's cancel — one action, one sentence,
          wherever it is reached from. */}
      <ConfirmDialog
        open={confirmingCancel}
        title="Cancel this generation?"
        description="The video won't be created, and this cannot be undone."
        confirmLabel="Cancel generation"
        cancelLabel="Keep generating"
        onConfirm={() => void handleCancel()}
        onCancel={() => setConfirmingCancel(false)}
      />

      {/* The dialog stays open on failure and says what happened. Closing it
          would leave the page showing a video the user believes they deleted,
          which is the one outcome worse than the delete not working. */}
      <ConfirmDialog
        open={confirmingDelete}
        title="Delete this video permanently?"
        description={
          deleteError ?? 'The video and its file are removed for good. This cannot be undone.'
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete permanently'}
        cancelLabel="Keep video"
        onConfirm={() => void handleDelete()}
        onCancel={() => {
          if (deleting) return;
          setDeleteError(null);
          setConfirmingDelete(false);
        }}
      />
    </section>
  );
}
